import React from "react";
import {
  Box,
  Button,
  Heading,
  HStack,
  Input,
  Link,
  Text,
  useNumberInput,
} from "@chakra-ui/react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";

import Web3 from "web3";
import logoImg from "../../assets/images/logo.png";
import formImg from "../../assets/images/form.png";
import team1Img from "../../assets/images/team1.png";
import team2Img from "../../assets/images/team2.png";
import team3Img from "../../assets/images/team3.png";
import twitterImg from "../../assets/images/twitter.png";
import openseaImg from "../../assets/images/opensea.png";
import { useApp } from "../../hooks/useApp";
import { AppStatus } from "../../types";
import { copyTextToClipboard } from "../../lib/utils";
import { PRICE_IN_ETHEREUM, SINGLE_ETHEREUM } from "../../constants";
import { isMobile } from "../../lib/isMobile";

const AnchorLink = require("react-anchor-link-smooth-scroll").default;
const Fade = require("react-reveal/Fade");

const MintFormBackground = styled.img`
  width: 453px;
  height: 479px;
`;

const TeamPhoto = styled.img`
  width: 316px;
  height: 316px;
  object-fit: cover;
`;

const SocialLinkImage = styled.img`
  width: 32px;
  height: 30px;
  object-fit: contain;
`;

const Logo = styled.img`
  width: 322px;
  height: 56px;
  object-fit: contain;
`;

const Timeline = styled.ul`
  position: relative;
  list-style: none;

  li {
    padding-bottom: 1.5rem;
    border-left: 1px solid #8d7349;
    position: relative;
    padding-left: 24px;
    margin-left: 10px;
    &:last-child {
      border: 0px;
      padding-bottom: 0;
    }
    &:before {
      content: "";
      width: 24px;
      height: 24px;
      background: linear-gradient(360deg, #8d7349 0%, #e2d391 100%);
      border-radius: 50%;
      position: absolute;
      left: -13px;
      top: 0px;
    }
  }
`;

function PageHeading({
  mb = 6,
  id,
  children,
}: {
  id?: string;
  mb?: number;
  children: any;
}) {
  return (
    <Fade bottom cascade>
      <Heading
        id={id}
        as="h3"
        mb={[2, 2, mb]}
        textAlign="center"
        css={css`
          background: -webkit-linear-gradient(#e2d391, #8d7349);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        `}
        fontWeight={400}
      >
        {children}
      </Heading>
    </Fade>
  );
}

function PageBlock({ bg, children }: { bg?: string; children: any }) {
  return (
    <Box
      bg={bg}
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      margin="0 auto"
      maxWidth={840}
      mb={12}
    >
      {children}
    </Box>
  );
}

function HookUsage({ inputRef }: { inputRef: any }) {
  const { getInputProps, getIncrementButtonProps, getDecrementButtonProps } =
    useNumberInput({
      step: 1,
      defaultValue: 1,
      min: 1,
      max: 20,
      precision: 0,
    });

  const inc = getIncrementButtonProps();
  const dec = getDecrementButtonProps();
  const input = getInputProps({ readOnly: true });

  return (
    <HStack mb={4} maxW="320px">
      <Button variant="ghost" {...dec}>
        -
      </Button>
      <Input
        size="lg"
        fontSize={36}
        fontFamily="heading"
        textAlign="center"
        {...input}
        ref={inputRef}
      />
      <Button variant="ghost" {...inc}>
        +
      </Button>
    </HStack>
  );
}

function TeamMember({
  imgSrc = team1Img,
  title,
  subtitle,
}: {
  imgSrc: string;
  title: string;
  subtitle: string;
}) {
  return (
    <Box
      as="li"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <TeamPhoto src={imgSrc} />
      <PageHeading mb={1}>{title}</PageHeading>
      <Text fontFamily="heading">{subtitle}</Text>
    </Box>
  );
}

function PageButton({
  size = "md",
  children,
  onPress,
}: {
  size?: "md" | "lg";
  children: any;
  onPress?: any;
}) {
  return (
    <Button
      onClick={onPress}
      size={size}
      css={css`
        background: linear-gradient(360deg, #8d7349 0%, #e2d391 100%);
        border-radius: 100px;
        font-weight: 400;

        &:hover {
          background: linear-gradient(360deg, #8c795b 0%, #ded2a6 100%);
        }
      `}
    >
      <Text fontFamily="heading">{children}</Text>
    </Button>
  );
}

function PageHeader() {
  return (
    <HStack
      flexWrap="wrap"
      flexDirection={["column", "column", "row"]}
      bg="black"
      position="sticky"
      top="0"
      zIndex={999}
      pb={4}
      margin="0 auto 1rem"
      px={8}
      pt={8}
      maxWidth={1440}
    >
      <Box pb={[4, 4, 0]}>
        <Logo src={logoImg} />
      </Box>
      <HStack
        pb={[4, 4, 0]}
        alignItems="center"
        justifyContent="center"
        flex={1}
      >
        <Link
          as={AnchorLink}
          offset="144"
          href="#lore"
          px={[1, 2, 4]}
          fontFamily="heading"
        >
          Lore
        </Link>
        <Link
          as={AnchorLink}
          offset="144"
          href="#examples"
          px={[1, 2, 4]}
          fontFamily="heading"
        >
          Example
        </Link>
        <Link
          as={AnchorLink}
          offset="144"
          href="#rarity"
          px={[1, 2, 4]}
          fontFamily="heading"
        >
          Rarity
        </Link>
        <Link
          as={AnchorLink}
          offset="144"
          href="#mint"
          px={[1, 2, 4]}
          fontFamily="heading"
        >
          Mint
        </Link>
        <Link
          as={AnchorLink}
          offset="144"
          href="#referral"
          px={[1, 2, 4]}
          fontFamily="heading"
        >
          Referral
        </Link>
      </HStack>
      <HStack spacing={2}>
        <Link href="https://twitter.com/SpellBook_NFT" target="_blank">
          <SocialLinkImage src={twitterImg} />
        </Link>
        <Link href="https://opensea.io/collection/polyspells" target="_blank">
          <SocialLinkImage src={openseaImg} />
        </Link>
      </HStack>
    </HStack>
  );
}

function Spellbook() {
  const inputRef = React.useRef();
  const searchParams = new URLSearchParams(location.search);
  const ref = searchParams.get("ref");
  const {
    mint,
    tokenPrice,
    wallet,
    mintedCount,
    mintRef,
    status,
    connect,
    switchToPolygon,
  } = useApp();

  return (
    <Box flex={1} h="100%" bg="black">
      <PageHeader />
      <Box px={[2, 1, 0]}>
        <PageBlock>
          <Fade bottom>
            <PageHeading id="lore">Lore</PageHeading>
          </Fade>
          <Fade bottom delay={500}>
            <Text textAlign="center">
              <p>
                A long time ago there was a secret order of futurist alchemists.
                The subject of their unification was the creation of spells and
                magical rituals, which they carefully created for future
                generations of alchemists.
              </p>
              <br />
              <p>
                They created 1000 cards with spells, which were later turned
                into NFT on Polygon network and now you can become one of the
                owners of the secret spells of the order.
              </p>
            </Text>
          </Fade>
        </PageBlock>
      </Box>
      <Fade bottom>
        <PageBlock>
          <PageHeading id="mint">Get your spell</PageHeading>
          {isMobile ? (
            <Text mb={4} fontFamily="heading">
              Use desktop with metamask to mint
            </Text>
          ) : (
            <Box mb={2} w={["100%", 453]} h={["auto", 479]} position="relative">
              <MintFormBackground src={formImg} />
              <Box
                position="absolute"
                left={0}
                top={0}
                right={0}
                bottom={0}
                display="flex"
                flexDirection="column"
                textAlign="center"
                alignItems="center"
                justifyContent="center"
              >
                {/* {status === AppStatus.Connected ? ( */}
                {/*  <Box mb={8}> */}
                {/*    <PageButton onPress={connect} size="lg"> */}
                {/*      Switch to polygon network */}
                {/*    </PageButton> */}
                {/*  </Box> */}
                {/* ) : null} */}
                {status === AppStatus.Connected ? (
                  <>
                    <Text mb={4} fontFamily="heading">
                      {mintedCount}/1000 Minted
                    </Text>
                    <HookUsage inputRef={inputRef} />
                  </>
                ) : null}
                {status === AppStatus.Disconnected ? (
                  <Box mb={8}>
                    <PageButton onPress={connect} size="lg">
                      Connect wallet
                    </PageButton>
                  </Box>
                ) : null}
                <Text mb={4} fontFamily="heading">
                  You can mint up to 20 spells. <br />
                  {(tokenPrice || PRICE_IN_ETHEREUM) &&
                    Web3.utils.fromWei(tokenPrice || PRICE_IN_ETHEREUM)}{" "}
                  Polygon (MATIC) per spell
                </Text>
                <PageButton
                  onPress={() => {
                    const amount = (inputRef.current as any)?.value;
                    if (ref) {
                      mintRef({ amount, ref });
                    } else {
                      mint(amount);
                    }
                  }}
                  size="lg"
                >
                  Mint a spell
                </PageButton>
              </Box>
              <Box
                cursor="pointer"
                position="absolute"
                left={0}
                right={0}
                bottom={[4, 2, 0]}
                opacity={0.9}
                transition="all .33s ease-in"
                onClick={() => {
                  const amount = (inputRef.current as any)?.value;
                  if (ref) {
                    mintRef({ amount, ref });
                  } else {
                    mint(amount);
                  }
                }}
                _hover={{
                  opacity: 1,
                  transform: "scale(1.01)",
                }}
              >
                <PageHeading>Mint a spell</PageHeading>
              </Box>
            </Box>
          )}
          <Text fontFamily="heading">
            NEW TO POLYGON?{" "}
            <Link
              href="#faq"
              textDecoration="underline"
              textDecorationColor="white"
            >
              READ FAQ
            </Link>
          </Text>
        </PageBlock>
      </Fade>
      <Box bg="#111111" mb={8} pt={8} pb={1}>
        <PageBlock bg="#111111">
          <PageHeading id="examples">Spells examples</PageHeading>
          <HStack
            mb={4}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            wrap={["wrap", "wrap", "nowrap"]}
          >
            <Box maxWidth={[140, 190, 285]}>
              <Fade bottom>
                <div className="reflection-container example1">
                  {new Array(100).fill(0).map((v, index) => (
                    <span
                      className={`reflection-grid-cell reflection-grid-cell-${
                        index + 1
                      }`}
                    />
                  ))}
                  <div className="reflection-content" />
                </div>
              </Fade>
            </Box>
            <Box>
              <Fade bottom delay={500}>
                <div className="reflection-container example2">
                  {new Array(100).fill(0).map((v, index) => (
                    <span
                      className={`reflection-grid-cell reflection-grid-cell-${
                        index + 1
                      }`}
                    />
                  ))}
                  <div className="reflection-content" />
                </div>
              </Fade>
            </Box>
            <Box>
              <Fade bottom delay={1e3}>
                <div className="reflection-container example3">
                  {new Array(100).fill(0).map((v, index) => (
                    <span
                      className={`reflection-grid-cell reflection-grid-cell-${
                        index + 1
                      }`}
                    />
                  ))}
                  <div className="reflection-content" />
                </div>
              </Fade>
            </Box>
          </HStack>
          <Link as={AnchorLink} _hover={{ textDecoration: "none" }} href="#ar">
            <PageButton>3D/AR Example</PageButton>
          </Link>
        </PageBlock>
      </Box>
      <Fade bottom>
        <PageBlock>
          <PageHeading id="rarity">Rarity</PageHeading>
          <Text textAlign="center">
            3 levels of spells - Regular, Great, Arcane <br />
            25 spell types <br />
            9 frames <br />7 additional elements
          </Text>
        </PageBlock>
      </Fade>
      <Fade bottom>
        <PageBlock>
          <PageHeading id="referral">Referral program</PageHeading>
          <Text mb={2} textAlign="center" fontFamily="heading">
            Earn 30% from each sale with your refferal code.{" "}
          </Text>
          {wallet ? (
            <Text
              overflowWrap="anywhere"
              mb={4}
              textAlign="center"
              fontFamily="heading"
            >
              https://polyspells.vercel.app/?ref={wallet}
            </Text>
          ) : (
            <Text
              overflowWrap="anywhere"
              mb={4}
              textAlign="center"
              fontFamily="heading"
            >
              Please, connect wallet first
            </Text>
          )}
          {wallet ? (
            <PageButton
              onPress={() => {
                copyTextToClipboard(
                  `https://polyspells.vercel.app/?ref=${wallet}`
                );
              }}
              size="lg"
            >
              Copy link
            </PageButton>
          ) : (
            <PageButton
              onPress={() => {
                connect();
              }}
              size="lg"
            >
              Connect wallet
            </PageButton>
          )}
        </PageBlock>
      </Fade>
      <Box ml={[2, 2, 0]} pl={[2, 1, 0]} pr={[2, 1, 0]}>
        <PageBlock>
          <PageHeading id="roadmap">Roadmap</PageHeading>
          <Timeline>
            <Fade top cascade>
              <li>
                <Text position="absolute" left={-16} fontFamily="heading">
                  40%
                </Text>
                <Text fontFamily="heading">Discord creation</Text>
              </li>
              <li>
                <Text position="absolute" left={-16} fontFamily="heading">
                  50%
                </Text>
                <Text fontFamily="heading">Airdrop 10 spells to holders</Text>
              </li>
              <li>
                <Text position="absolute" left={-16} fontFamily="heading">
                  80%
                </Text>
                <Text fontFamily="heading">10 top refferals get 20 spells</Text>
              </li>
              <li>
                <Text position="absolute" left={-16} fontFamily="heading">
                  100%
                </Text>
                <Text fontFamily="heading">
                  Start of alchemy game creation <br /> and Mana coins for
                  Spells holders
                </Text>
              </li>
            </Fade>
          </Timeline>
        </PageBlock>
      </Box>
      <PageBlock>
        <PageHeading id="team">Team</PageHeading>
        <HStack flexDirection={["column", "row", "row"]} as="ul">
          <Fade top cascade>
            <TeamMember
              imgSrc={team1Img}
              title="Vadslav"
              subtitle="Art & alchemy"
            />
            <TeamMember
              imgSrc={team2Img}
              title="Rif"
              subtitle="Development magic"
            />
            <TeamMember
              imgSrc={team3Img}
              title="Gregor"
              subtitle="Community & relations"
            />
          </Fade>
        </HStack>
      </PageBlock>
      <Box pl={4} mr={2}>
        <PageBlock>
          <PageHeading id="faq">FAQ</PageHeading>
          <Text
            css={`
              li {
                margin-bottom: 0.75rem;
              }
              p {
                margin-bottom: 0.5rem;
              }
              button p {
                margin-bottom: 0;
              }
            `}
            fontFamily="heading"
          >
            <strong>How to get started on Polygon network?</strong>
            <ol>
              <Fade top cascade>
                <li>
                  Get a{" "}
                  <Link
                    target="_blank"
                    href="https://wallet.polygon.technology/"
                  >
                    Metamask Wallet
                  </Link>
                  . If you already have it, go to the next step
                </li>
                <li>
                  <p>Add Polygon network to your metamask</p>
                  <Link
                    _hover={{ textDecoration: "none" }}
                    onClick={() => switchToPolygon}
                  >
                    <PageButton>SWITCH TO POLYGON</PageButton>
                  </Link>
                </li>
                <li>
                  <p>Connect your MetaMask to the Polygon wallet</p>
                  <Link
                    _hover={{ textDecoration: "none" }}
                    target="_blank"
                    href="https://wallet.polygon.technology/wallet"
                  >
                    <PageButton>GO TO POLYGON WALLET</PageButton>
                  </Link>
                </li>
                <li>
                  Buy MATIC on any exchange like Binance, Coinbase, Kucoin and
                  others
                </li>
                <li>
                  <p>You are ready to GO!</p>
                  <Link _hover={{ textDecoration: "none" }} href="#mint">
                    <PageButton>MINT A SPELL</PageButton>
                  </Link>
                </li>
              </Fade>
            </ol>
          </Text>
        </PageBlock>
      </Box>
      <Box id="ar">
        {/**
       @ts-ignore* */}
        <model-viewer
          alt="Neil Armstrong's Spacesuit from the Smithsonian Digitization Programs Office and National Air and Space Museum"
          src="card.glb"
          ar
          ar-modes="scene-viewer webxr quick-look"
          camera-controls
          environment-image="neutral"
          poster="poster.webp"
          style={{ width: "100vw", height: "66vh" }}
          shadow-intensity="1"
        />
      </Box>
    </Box>
  );
}

export { Spellbook };
